import { ForceGraph3DInstance } from "3d-force-graph";
import { Ease, Power0, TweenMax } from "gsap";
import { OrbitControls } from "three/examples/jsm/controls/OrbitControls";
import { debug as d } from "debug";
import nullForce from "./null-force";
import { NodePosition, NodePositionMap } from "./node-layouts";

const debug = d("cs.heatmaps");

export function hideLinks(graph: ForceGraph3DInstance): void {
  debug("hideLinks");
  graph.linkVisibility(false);
}

export function showLinks(
  graph: ForceGraph3DInstance,
  linkEnableFn: (link: { source?: any; target?: any }) => boolean,
): void {
  debug("showLinks");
  graph.linkVisibility(linkEnableFn);
}

export function freezeSimulation(graph: ForceGraph3DInstance): void {
  graph.d3AlphaMin(1); // Freeze simulation (but not rendering/interaction)
}

export function unfreezeSimulation(graph: ForceGraph3DInstance): void {
  graph.d3AlphaMin(0.0000001);
  graph.d3ReheatSimulation();
}

export function disableSimulationForces(graph: ForceGraph3DInstance): void {
  graph.d3Force("link")!.strength(() => 0); // Required to be present, but effectively disable it
  graph.d3Force("center", nullForce());
  graph.d3Force("charge", nullForce());
}

export function setAllNodePositions(nodes: any[], position: NodePosition): void {
  nodes.forEach((n, i) => {
    n.x = position.x;
    n.y = position.y;
    n.z = position.z;
    n.vx = 0;
    n.vy = 0;
    n.vz = 0;
  });
}

export function setControlsEnabled(graph: ForceGraph3DInstance, enabled: boolean): void {
  const controls = graph.controls() as OrbitControls;
  controls.enabled = enabled;
}

type AnimateNodesOptions = {
  transitionMs: number;
  delayMs?: number;
  ease?: Ease;
  individualFinalPositions?: NodePositionMap;
  commonFinalPosition?: NodePosition;
  commonInitialPosition?: NodePosition;
  hideLinksDuringTransition?: boolean;
};

export function animateNodesToPositions(
  graph: ForceGraph3DInstance,
  nodes: { id?: string | number | undefined; x?: number; y?: number; z?: number }[],
  options: AnimateNodesOptions,
  onCompleteCallback: () => void,
): void {
  if (!options.individualFinalPositions && !options.commonFinalPosition) {
    throw new Error("Either individual positions or a common position must be specified");
  }

  if (options.hideLinksDuringTransition) hideLinks(graph);
  unfreezeSimulation(graph);
  nodes.forEach((node, i) => {
    const finalPosition = options.individualFinalPositions
      ? options.individualFinalPositions[node.id!]!
      : options.commonFinalPosition;
    if (!finalPosition) {
      throw new Error(`Failed to determine final position for node ID ${node.id}`);
    }
    const { x, y, z } = finalPosition;
    const toVars = {
      x,
      y,
      z,
      ease: options.ease || Power0.easeOut,
      delay: (options.delayMs || 0) / 1000,
    };
    const tween = options.commonInitialPosition
      ? TweenMax.fromTo(node, options.transitionMs / 1000, options.commonInitialPosition, toVars)
      : TweenMax.to(node, options.transitionMs / 1000, toVars);
    if (i === 0) {
      tween.eventCallback("onComplete", () => {
        onCompleteCallback();
        freezeSimulation(graph);
      });
    }
  });
}
